import React, { useEffect, useMemo } from 'react';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { observer } from 'mobx-react-lite';
import { Spinner, ToastManager as IMToastManager, ThemeContext } from '@insurancemenu/imenu-components-lib';
import { refreshToken } from 'api/refreshToken';
import { useRootStore } from 'store/rootStore';
import { createThemeContext } from '@insurancemenu/imenu-components-lib/dist/hooks/useTheme';
import { eeRights } from '@insurancemenu/imenu-components-lib/dist/utils/accessRights';
import { toJS } from 'mobx';
import AppRouter from './routes/AppRouter';
import { SpinnerWrapperStyled, AppWrapperStyled } from './globalStyles';

const App: React.FC = observer(() => {
  const {
    domainStoreV2: { loading, getDomainById, domainHeader },
    userStore: { sponsorId, getHeaderData, userRoles },
    agentsStore: { requestAgents, setLoading: setAgentsLoading, agentsOfRecord, isLoading: agentsLoading },
  } = useRootStore();

  const convertedUserRoles = useMemo(() => userRoles.slice().map((role) => toJS(role)), [userRoles]);

  const agentsOfRecordCount = agentsOfRecord.length;

  useEffect(() => {
    // TypeScript doesn't support the cookieStore API yet, so we need to use a type assertion
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
    const cookieStore = (
      window as {
        cookieStore?: {
          addEventListener: (event: string, handler: (e: { changed?: Array<{ name: string }> }) => void) => void;
          removeEventListener: (event: string, handler: (e: { changed?: Array<{ name: string }> }) => void) => void;
        };
      }
    ).cookieStore;

    const onCookieChange = (e: { changed?: Array<{ name: string }> }): void => {
      if (e.changed?.find((c) => c.name === 'login_time')) {
        window.location.href = `https://${process.env.REACT_APP_AUTH_FE_BASE_URL}/login`;
      }
    };

    if (cookieStore) {
      cookieStore?.addEventListener('change', onCookieChange);
    }
    return (): void => {
      if (cookieStore) {
        cookieStore.removeEventListener('change', onCookieChange);
      }
    };
  }, []);

  const themeContextValue = useMemo(() => {
    return createThemeContext(domainHeader.headerStyleId);
  }, [domainHeader.headerStyleId]);

  async function refreshTokenAsync(): Promise<void> {
    try {
      await refreshToken();
    } catch (error) {
      window.location.href = `https://${process.env.REACT_APP_AUTH_FE_BASE_URL}/login`;
    }
  }

  useEffect(() => {
    const interval = setInterval(() => {
      void refreshTokenAsync();
    }, 300000); // 300 000 ms - 5 mins

    return (): void => clearInterval(interval);
  }, []);

  useEffect(() => {
    void getHeaderData();
  }, []);

  // It's a dirty hack to prevent this request on employee portal.
  // Actually, this logic should be moved on one level lower (BP-specific) and
  // a separate entry point for employer/employee should be created.
  // All the employee-specific routes should be transferred down there.
  useEffect(() => {
    if (convertedUserRoles.length > 0) {
      if (!eeRights(convertedUserRoles) && agentsOfRecordCount === 0) {
        void requestAgents();
      } else {
        setAgentsLoading(false);
      }
    } else {
      setAgentsLoading(false);
    }
  }, [convertedUserRoles, agentsOfRecordCount, requestAgents, setAgentsLoading]);

  useEffect(() => {
    if (sponsorId) {
      // Pass domain id explicitly, instead of grabbing from Cookies, because if an EE belongs to 2 groups in 2 different sponsors, cookies for such ee won't change
      void getDomainById(sponsorId);
    }
  }, [sponsorId]);

  return (
    <ThemeContext.Provider value={themeContextValue}>
      {loading || agentsLoading ? (
        <SpinnerWrapperStyled>
          <Spinner />
        </SpinnerWrapperStyled>
      ) : (
        <AppWrapperStyled>
          <AppRouter />
        </AppWrapperStyled>
      )}

      <ToastContainer />
      <IMToastManager />
    </ThemeContext.Provider>
  );
});

export default App;
